import Box from '@mui/material/Box';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Closed-interval date range filter (PLAN §3.3 / §1.5).
 *
 * Two `DatePicker`s (from/to) on the dayjs adapter. Both bounds are
 * inclusive. `maxRangeDays` caps the span (default 90) — picking a `to`
 * that exceeds the cap from the current `from` is blocked at the picker
 * level (`maxDate`), so the backend's 400-on-too-wide guard is rarely
 * hit. ISO date strings cross the `onChange` boundary (YYYY-MM-DD).
 */
export interface DateRangeFilterProps {
  /** ISO date (YYYY-MM-DD) or null. */
  from: string | null;
  to: string | null;
  onChange: (range: { from: string | null; to: string | null }) => void;
  /** Max allowed span between from/to in days. */
  maxRangeDays?: number;
}

const toIso = (d: Dayjs | null): string | null =>
  d && d.isValid() ? d.format('YYYY-MM-DD') : null;

export const DateRangeFilter = ({
  from,
  to,
  onChange,
  maxRangeDays = 90,
}: DateRangeFilterProps): ReactElement => {
  const { t } = useTranslation();
  const fromDay = useMemo(() => (from ? dayjs(from) : null), [from]);
  const toDay = useMemo(() => (to ? dayjs(to) : null), [to]);

  // Cap the `to` picker so the admin can't pick a span wider than allowed.
  const maxToDate = useMemo(
    () => (fromDay ? fromDay.add(maxRangeDays, 'day') : undefined),
    [fromDay, maxRangeDays],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <DatePicker
          label={t('filters.dateFrom')}
          value={fromDay}
          {...(toDay ? { maxDate: toDay } : {})}
          onChange={(next) => onChange({ from: toIso(next), to })}
          slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
        />
        <DatePicker
          label={t('filters.dateTo')}
          value={toDay}
          {...(fromDay ? { minDate: fromDay } : {})}
          {...(maxToDate ? { maxDate: maxToDate } : {})}
          onChange={(next) => onChange({ from, to: toIso(next) })}
          slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
        />
      </Box>
    </LocalizationProvider>
  );
};
